/**
 * QuestionResultIndicator - Shared Result Display Component
 *
 * Single responsibility: Displaying correct/wrong result icons.
 * Reusable across all question types (TeamPick, PlayerPick, PropPick).
 */

import React from 'react';
import CorrectIcon from '../CorrectIcon';
import WrongIcon from '../WrongIcon';

interface QuestionResultIndicatorProps {
  /**
   * Whether the selection was correct
   */
  isCorrect: boolean;

  /**
   * Position of the indicator (left or right side of the card)
   * @default 'left'
   */
  position?: 'left' | 'right';

  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Displays a correct or wrong icon indicator for question results
 *
 * @example
 * ```tsx
 * <QuestionResultIndicator isCorrect={true} position="left" />
 * <QuestionResultIndicator isCorrect={false} position="right" />
 * ```
 */
export const QuestionResultIndicator: React.FC<QuestionResultIndicatorProps> = ({
  isCorrect,
  position = 'left',
  className = '',
}) => {
  const positionClass = position === 'left' ? 'top-2 left-2' : 'top-2 right-2';

  return (
    <div className={`absolute ${positionClass} ${className}`}>
      {isCorrect ? <CorrectIcon /> : <WrongIcon />}
    </div>
  );
};
